import type { Paginated } from "@/@types/paginated";
import { Tags } from "@/enum/tags";
import type { PayrollVoucherStatusEnum } from "@/enum/payroll-voucher-status";
import type {
  SortDirection,
  VoucherSortColumn,
} from "@/pages/conciliation/payroll-voucher-approval/constants";
import { toQueryParams } from "@/utils/to-query-params";
import {
  blobDownloadResponseHandler,
  type BlobDownload,
  type EmptyResponse,
} from "@/utils/blob-response-handler";
import { apiSlice } from "./http";

export interface PayrollVoucherResponse {
  id: number;
  conciliation_id: number;
  conciliation_date: string;
  employee_name: string;
  employee_cpf: string;
  store_code: string;
  amount: number;
  installments_count: number;
  analyst_justification: string;
  status: PayrollVoucherStatusEnum;
  rejection_reason: string | null;
  decided_by_email: string | null;
  conciliation_closed_at: string | null;
  version: number;
}

export interface PayrollVoucherListParams {
  start: string;
  end: string;
  status?: PayrollVoucherStatusEnum;
  page: number;
  perPage: number;
  sort?: VoucherSortColumn;
  direction?: SortDirection;
}

export interface PayrollVoucherStatusCounts {
  all: number;
  pending: number;
  approved: number;
  rejected: number;
}

/**
 * Ciclo do RH a exportar, no formato `AAAA-MM`. Ausente, a API usa o ciclo em
 * curso (WI 12811).
 */
export interface PayrollFiscalCycleExportArgs {
  cycle?: string;
}

export interface PayrollVoucherCycleResponse {
  // Rótulo do ciclo do RH em curso. Quem decide qual é ele é a API — o Front
  // apenas compara rótulos (regra 5).
  cycle: string;
  interval: string;
  days_to_close: number;
  vouchers_in_cycle: number;
  decided_percentage: number;
  payroll_discount_in_cycle: number;
  exported_percentage: number;
}

export interface DecidePayrollVoucherBody {
  status: PayrollVoucherStatusEnum;
  version: number;
  decision_comment: string;
  rejection_reason: string;
}

export interface DecidePayrollVoucherArgs {
  id: number;
  body: DecidePayrollVoucherBody;
}

export interface DecidePayrollVoucherBatchItem extends DecidePayrollVoucherBody {
  id: number;
}

export interface PayrollVoucherBatchSkip {
  id: number;
  reason: string;
}

export interface PayrollVoucherBatchDecided {
  id: number;
  status: string;
  version: number;
}

export interface PayrollVoucherBatchResult {
  updated: PayrollVoucherBatchDecided[];
  skipped: PayrollVoucherBatchSkip[];
  installments_created: number;
}

const RESOURCE_URL: string = "/payroll-voucher";

export const payrollVoucherApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPayrollVouchers: builder.query<
      Paginated<PayrollVoucherResponse>,
      PayrollVoucherListParams
    >({
      query: (filters) => ({
        url: `${RESOURCE_URL}?${toQueryParams(filters)}`,
        method: "GET",
      }),
      providesTags: [Tags.PAYROLL_VOUCHER],
    }),

    getPayrollVoucherStatusCounts: builder.query<
      PayrollVoucherStatusCounts,
      { start: string; end: string }
    >({
      query: (range) => ({
        url: `${RESOURCE_URL}/status-counts?${toQueryParams(range)}`,
        method: "GET",
      }),
      providesTags: [Tags.PAYROLL_VOUCHER],
    }),

    getPayrollVoucherCycle: builder.query<PayrollVoucherCycleResponse, void>({
      query: () => ({
        url: `${RESOURCE_URL}/cycle/summary`,
        method: "GET",
      }),
      providesTags: [Tags.PAYROLL_VOUCHER_CYCLE],
    }),

    exportPayrollRange: builder.query<
      BlobDownload | EmptyResponse,
      { start: string; end: string }
    >({
      query: (range) => ({
        url: `${RESOURCE_URL}/export?${toQueryParams(range)}`,
        method: "GET",
        responseHandler: (response: Response) =>
          blobDownloadResponseHandler<EmptyResponse>(response),
      }),
    }),

    exportPayrollFiscalCycle: builder.mutation<
      BlobDownload | EmptyResponse,
      PayrollFiscalCycleExportArgs | void
    >({
      query: (args: PayrollFiscalCycleExportArgs | void) => ({
        url: args?.cycle
          ? `/payroll-export/fiscal-cycle?cycle=${args.cycle}`
          : "/payroll-export/fiscal-cycle",
        method: "GET",
        responseHandler: (response: Response) =>
          blobDownloadResponseHandler<EmptyResponse>(response),
      }),
      invalidatesTags: [Tags.PAYROLL_VOUCHER, Tags.PAYROLL_VOUCHER_CYCLE],
    }),

    decidePayrollVoucher: builder.mutation<
      PayrollVoucherResponse,
      DecidePayrollVoucherArgs
    >({
      query: ({ id, body }) => ({
        url: `${RESOURCE_URL}/${id}/decision`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        Tags.PAYROLL_VOUCHER,
        Tags.PAYROLL_VOUCHER_CYCLE,
        Tags.CONCILIATION_HISTORY,
      ],
    }),

    decidePayrollVoucherBatch: builder.mutation<
      PayrollVoucherBatchResult,
      { items: DecidePayrollVoucherBatchItem[] }
    >({
      query: ({ items }) => ({
        url: `${RESOURCE_URL}/decision/batch`,
        method: "PUT",
        body: { items },
      }),
      invalidatesTags: [
        Tags.PAYROLL_VOUCHER,
        Tags.PAYROLL_VOUCHER_CYCLE,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
  }),
});

export const {
  useGetPayrollVouchersQuery,
  useLazyExportPayrollRangeQuery,
  useExportPayrollFiscalCycleMutation,
  useGetPayrollVoucherCycleQuery,
  useGetPayrollVoucherStatusCountsQuery,
  useDecidePayrollVoucherMutation,
  useDecidePayrollVoucherBatchMutation,
} = payrollVoucherApi;
